import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * MENTOR AP-8 (§7a) — Folgen-Hilfe „Muster pausieren/loeschen" (E-Del-1).
 *
 * Kontextuelle `?`-Tour fuer den `delete-pattern-dialog`. Der Dialog oeffnet
 * aus dem Muster-Zeilenmenue/⋯ — kein deterministischer Opener-Anker, daher
 * KEIN `launchContext`: nur ueber den In-Dialog-`?`-Button startbar.
 *
 * Folgenmatrix §7a: Vergangenheit bleibt, zukuenftige Vorkommen verschwinden,
 * Betroffene koennen informiert werden. Der „Lieber nur pausieren"-Schritt ist
 * die bevorzugte reversible Alternative — er skippt, wenn das Muster bereits
 * pausiert ist (dann fehlt der Pause-Button im DOM).
 */
const DIALOG_CTX = { attachToDialog: true, autoCloseOnDialogClose: true };

export const DELETE_PATTERN_HELP_TOUR: TourConfig = {
  id: TourFeature.DeletePatternHelp,
  version: 1,
  title: 'TUTORIAL.DELETE_PATTERN.TITLE',
  description: 'TUTORIAL.DELETE_PATTERN.DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 75,
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'TUTORIAL.DELETE_PATTERN.TITLE',
      steps: [
        {
          // Folgen-Karte: was stoppt, was verschwindet, was geschuetzt bleibt.
          id: 'consequences',
          anchor: AnchorId.DeletePatternConsequences,
          spotlightAnchor: AnchorId.DeletePatternDialog,
          title: 'TUTORIAL.DELETE_PATTERN.CONSEQUENCES.TITLE',
          content: 'TUTORIAL.DELETE_PATTERN.CONSEQUENCES.CONTENT',
          placement: 'bottom',
          dialogContext: { ...DIALOG_CTX, popoverOutsideDialog: true },
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          // Bevorzugte reversible Alternative — nur wenn Muster aktiv ist.
          id: 'pause',
          anchor: AnchorId.DeletePatternPause,
          spotlightAnchor: AnchorId.DeletePatternPause,
          title: 'TUTORIAL.DELETE_PATTERN.PAUSE.TITLE',
          content: 'TUTORIAL.DELETE_PATTERN.PAUSE.CONTENT',
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          // Pause-Button existiert nur bei aktivem Muster (data.isActive).
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="delete-pattern-pause"]'),
        },
        {
          // Mitarbeiterwirkung: Betroffene ueber den Wegfall informieren.
          id: 'notify',
          anchor: AnchorId.DeletePatternNotify,
          spotlightAnchor: AnchorId.DeletePatternNotify,
          title: 'TUTORIAL.DELETE_PATTERN.NOTIFY.TITLE',
          content: 'TUTORIAL.DELETE_PATTERN.NOTIFY.CONTENT',
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          // Bewusster Abschluss (Anzahl betroffener Termine im Button).
          id: 'confirm',
          anchor: AnchorId.DeletePatternConfirm,
          spotlightAnchor: AnchorId.DeletePatternConfirm,
          title: 'TUTORIAL.DELETE_PATTERN.CONFIRM.TITLE',
          content: 'TUTORIAL.DELETE_PATTERN.CONFIRM.CONTENT',
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
      ],
    },
  ],
};
